/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Введение</h2>
    <p className={styles.description}>
      Когда мы говорим о программировании, первое, что приходит в голову - это набор инструкций в файле — исходный код.
    </p>
    <p className={styles.description}>
      <b>Исходный код (source code)</b> — набор фраз, слов, специальных символов и т. д., специфичных для языка
      программирования, описывающих набор инструкций для компьютера.
    </p>
    <p className={styles.description}>
      Но символы, слова и фразы, которые составляют программу, на самом деле непонятны для машины. Есть шаг,
      выполняющийся после написания программы, который конвертирует исходный код в файле в набор инструкций понятных
      компьютеру. Этим занимается специальная программа: <b>компилятор</b> или <b>интерпретатор</b>.
    </p>
    <p className={styles.description}>
      Отсюда можно сделать вывод - код пишется не для машины, а для разработчика. Исходный код должен не только верно
      решать задачу, но и иметь смысл, быть понятным и легко читаемым. Это одна из самых сложных задач для начинающих (и
      не только), на ее решение уйдет много времени и сил.
    </p>
    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Логическое мышление</h2>
      <p className={styles.description}>
        В мире существует много (сотни) языков программирования. На самом деле, они не такие сложные как человеческие,
        потому что состоят из довольно маленького набора синтаксических конструкций, а их принцип работы и базовые
        концепции похожи между разными языками.
      </p>
      <p className={styles.description}>
        Опытные разработчики рассматривают проблемы с точки зрения алгоритмов — набора шагов, которые нужно выполнить
        для достижения определённой цели, даже если детали меняются.
      </p>
      <p className={styles.description}>
        Мы постоянно используем алгоритмы в повседневной жизни. Например процесс приготовления чая - это набор
        необходимых шагов (алгоритм) для достижения результата. Когда вы привыкнете выстраивать решение задачи в виде
        алгоритма, язык программирования будет всего лишь инструментом.
      </p>
    </section>
    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - JavaScript</h2>
      <p className={styles.description}>
        <b>JavaScript</b> — реализация спецификации <code className={styles.code}>EcmaScript</code>, высокоуровневый
        язык программирования, поддерживаемый всеми современными веб-браузерами. Изначально предназначенный для
        взаимодействия с элементами веб-страниц и добавления интерактивности.
      </p>
      <p className={styles.notificationError}>
        <code className={styles.codeError}>JavaScript</code> не имеет никакого отношения к языку программирования{' '}
        <code className={styles.codeError}>Java</code>. Это два независимых языка программирования с различным
        синтаксисом и принципами работы. Путать их в среде разработчков будет верхом профессиональной неграмотности.
      </p>
      <p className={styles.description}>
        При Front-end разработке, JavaScript используется с HTML и CSS для обеспечения функциональности веб-страницы,
        такой как:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>несложные вычисления</li>
        <li className={styles.list__item}>проверка и манипуляция введенными пользователем данными</li>
        <li className={styles.list__item}>хранение информации в браузере пользователя</li>
        <li className={styles.list__item}>динамическое изменение HTML-документа</li>
        <li className={styles.list__item}>реакция на действия пользователя</li>
        <li className={styles.list__item}>создание интерактивных элементов: галлерей, графиков и т. д.</li>
        <li className={styles.list__item}>взаимодействие с сервером и базой данных</li>
      </ul>
      <p className={styles.notificationInfo}>
        Если JavaScript-код пишется для исполнения в браузере (Front-end), у разработчика нет инструментов и доступа к
        файловой или операционной системе по соображениям безопасности.
      </p>
      <p className={styles.description}>На сегодняшний день используя JavaScript можно создавать:</p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          веб-приложения используя фреймворки <code className={styles.code}>React</code>,{' '}
          <code className={styles.code}>Vue</code>, <code className={styles.code}>Angular</code> и другие
        </li>
        <li className={styles.list__item}>
          бекенд-приложения на <code className={styles.code}>Node.js</code>
        </li>
        <li className={styles.list__item}>
          мобильные приложения используя <code className={styles.code}>React Native</code> или{' '}
          <code className={styles.code}>Ionic</code>
        </li>
        <li className={styles.list__item}>
          десктоп-приложения с помощью <code className={styles.code}>Electron</code>
        </li>
        <li className={styles.list__item}>
          микроконтроллеры с <code className={styles.code}>Johnny-Five</code> и{' '}
          <code className={styles.code}>Espruino</code>
        </li>
      </ul>
      <p className={styles.description}>
        Для реализации всех этих возможностей необходимо хорошо знать синтаксис языка, его особенности и механизмы, а
        так же тренироваться мыслить алгоритмически и решать как можно больше задач.
      </p>
    </section>
  </section>
))
